import { ArrowUpRight, MoreHorizontal, Settings, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { confirmModal } from '@/components/ui/modal'
import { useAuth } from '@/lib/auth/AuthProvider'
import { useDeleteProject } from '../api'
import { PROJECT_WRITE_PERM } from '../permissions'
import type { Project } from '../types'
import { toastError } from '../ui'
import { ProjectIcon, ProjectStatusBadge } from './ProjectBits'

type Props = { project: Project; onEdit: () => void; onDeleted: () => void }

export function ProjectHeader({ project: p, onEdit, onDeleted }: Props) {
  const { t } = useTranslation()
  const { can } = useAuth()
  const remove = useDeleteProject()
  const canWrite = can(PROJECT_WRITE_PERM)

  const onDelete = async () => {
    const ok = await confirmModal({
      title: t('projects.deleteTitle'),
      description: t('projects.deleteBody', { name: p.name, members: p.team.length, items: p.tasks.length }),
      confirmLabel: t('common.delete'),
      destructive: true,
    })
    if (!ok) return
    try {
      await remove.mutateAsync(p.id)
      toast.success(t('projects.deleted'))
      onDeleted()
    } catch (e) {
      toastError(e)
    }
  }

  return (
    <div className="flex flex-col gap-4 border-b p-4 sm:p-5 lg:flex-row lg:items-center">
      <div className="flex min-w-0 flex-1 items-start gap-4">
        <ProjectIcon icon={p.icon} color={p.color} className="size-14" />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold tracking-tight sm:text-xl">{p.name}</h2>
            <ProjectStatusBadge status={p.status} />
          </div>
          <p className="text-sm text-muted-foreground">{p.description}</p>
        </div>
        {canWrite ? (
          <div className="flex shrink-0 items-center gap-2 lg:hidden">
            <ActionMenu onEdit={onEdit} onDelete={() => void onDelete()} />
          </div>
        ) : null}
      </div>

      <Button asChild className="w-full sm:w-auto">
        <Link to={`/projects/${p.id}`}>
          {t('projects.openDetail')}
          <ArrowUpRight />
        </Link>
      </Button>
      {canWrite ? (
        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <Button variant="outline" onClick={onEdit}>
            <Settings />
            {t('projects.settings')}
          </Button>
          <ActionMenu onEdit={onEdit} onDelete={() => void onDelete()} />
        </div>
      ) : null}
    </div>
  )
}

function ActionMenu({ onEdit, onDelete }: { onEdit: () => void; onDelete: () => void }) {
  const { t } = useTranslation()
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" aria-label={t('projects.moreActions')}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={onEdit}>
          <Settings />
          {t('projects.settings')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={onDelete} className="text-destructive [&_svg]:text-destructive">
          <Trash2 />
          {t('projects.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
